
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AccountDeletion = () => {
  const [email, setEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!confirmed) {
      toast.error("Please confirm you want to delete your account");
      return;
    }
    setSubmitted(true);
    // Clear local access data
    localStorage.removeItem('appAccess');
    toast.success("Account deletion request submitted");
  };

  return (
    <div className="container mx-auto px-4 py-16 min-h-screen">
      <a
        href="/"
        className="mb-8 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to home
      </a>

      <div className="max-w-lg mx-auto">
        <h1 className="text-3xl font-bold mb-6">Account Deletion</h1>

        {submitted ? (
          <div className="bg-green-50 border border-green-200 rounded-md p-6">
            <h2 className="text-xl font-semibold text-green-800 mb-2">Request Received</h2>
            <p className="text-green-700">
              Your account deletion request has been submitted. Your local app data has been cleared.
              If you registered with an email, Aurora Multimedia will process your deletion request
              within 30 days. You will receive a confirmation email once your data has been removed.
            </p>
            <p className="text-green-700 mt-4">
              For any questions, contact us at{" "}
              <a href="mailto:info@auroramm.com" className="underline">info@auroramm.com</a>.
            </p>
          </div>
        ) : (
          <>
            <p className="mb-6 text-muted-foreground">
              If you would like to delete your account and associated data from the Aurora Multimedia App,
              please enter your email address below and confirm your request.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter the email you registered with"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="confirm"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1"
                />
                <Label htmlFor="confirm" className="text-sm font-normal">
                  I understand that this will permanently delete my account and all associated data.
                  This action cannot be undone.
                </Label>
              </div>

              <Button type="submit" variant="destructive" className="w-full">
                Delete My Account
              </Button>
            </form>

            <div className="mt-8 text-sm text-muted-foreground">
              <p>
                For questions about data deletion, contact us at{" "}
                <a href="mailto:info@auroramm.com" className="text-primary hover:underline">
                  info@auroramm.com
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountDeletion;
